import type { CoprincipalAccountSource } from "owa-account-source-list-store";
import { getArchiveMailboxInfo, getCoprincipalAccountByMailboxGuid, getCoprincipalAccountForAccountKey, getCoprincipalAccountsByOidAndTid, getCoprincipalAccountsByPuidAndTid, getSharedMailboxInfo, tryGetGlobalSettingsAccountMailboxInfo, } from "owa-account-source-list-store";
import waitForAccountToBeStarted from "owa-account-source-list/lib/accountStateChanged/waitForAccountToBeStarted";
import { lazyEnsureSharedMailboxDataForMailboxInfo } from "owa-account-source-list/lib/lazyFunctions";
import { logGreyError, logUsage } from "owa-analytics";
import { StepsCustomData } from "owa-analytics-record-steps";
import { getAccountKeyForSourceId, type MailboxInfo } from "owa-client-types";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { getQueryStringParameter } from "owa-querystring";

/**
 * Supported route key prefix types.
 * - MBX: identifies a mailbox by GUID
 * - OID: identifies an account by AAD Object ID
 * - PUID: identifies an account by Passport Unique ID
 */
type RouteKeyPrefix = "MBX" | "OID" | "PUID";
export type UniversalLinkRouteKeyType = RouteKeyPrefix | "SMTP" | "Other";

interface ParsedRouteKey {
    prefix: RouteKeyPrefix;
    /** The identifier value (GUID, OID, or PUID) */
    value: string;
    /** Optional tenant ID (portion after @) */
    tenantId?: string;
}

/**
 * Parses a URL-encoded route key into its prefix, value, and optional tenant ID.
 *
 * Route keys in Universal Links arrive URL-encoded in path segments.
 * Supported formats:
 * - `MBX:<mailboxGuid>@<tenantId>` — resolve by mailbox GUID
 * - `OID:<objectId>@<tenantId>` — resolve by AAD Object ID + tenant
 * - `PUID:<puid>@<tenantId>` — resolve by Passport Unique ID + tenant
 *
 * All formats may be URL-encoded (e.g., `MBX%3Aguid%40tenantId`).
 *
 * @param routeKey The raw URL-encoded route key from the URL path
 * @returns Parsed route key components, or undefined if the format is unrecognized
 */
export function parseRouteKey(routeKey: string): ParsedRouteKey | undefined {
    let decoded: string;
    try {
        decoded = decodeURIComponent(routeKey);
    }
    catch {
        return undefined;
    }
    
    const prefixMatch = decoded.match(/^(MBX|OID|PUID):(.+)$/i);
    if (!prefixMatch) {
        return undefined;
    }
    
    const prefix = prefixMatch[1].toUpperCase() as RouteKeyPrefix;
    const remainder = prefixMatch[2];
    
    const atIndex = remainder.indexOf("@");
    if (atIndex !== -1) {
        const value = remainder.substring(0, atIndex);
        const tenantId = remainder.substring(atIndex + 1);
        if (!value) {
            return undefined;
        }
        return { prefix, value, tenantId: tenantId || undefined };
    }
    
    return { prefix, value: remainder };
}

export function getUniversalLinkRouteKeyType(routeKey: string): UniversalLinkRouteKeyType {
    let decodedRouteKey: string;
    try {
        decodedRouteKey = decodeURIComponent(routeKey);
    }
    catch {
        return "Other";
    }
    
    const prefix = decodedRouteKey.match(/^([^:]+):/)?.[1].toUpperCase();
    switch (prefix) {
        case "MBX":
        case "OID":
        case "PUID":
        case "SMTP":
            return prefix;
        default:
            return "Other";
    }
}

/**
 * Extracts the mailbox GUID from a URL-encoded route key with MBX: prefix.
 *
 * Route keys in Universal Links arrive URL-encoded in path segments
 * (e.g., `MBX%3A80db1853-38c7-4ff3-945b-1e9a78119cb0%40ab9f5dac-...`).
 * This function decodes the value and extracts the GUID from the MBX: prefix.
 *
 * @param routeKey The raw URL-encoded route key from the URL path
 * @returns The extracted mailbox GUID, or undefined if the MBX: prefix is not present. (This would be unexpected, considering we should fail reaching here if the prefix doesn't exist)
 */
export function extractMailboxGuidFromRouteKey(routeKey: string): string | undefined {
    const parsed = parseRouteKey(routeKey);
    if (!parsed || parsed.prefix !== "MBX") {
        return undefined;
    }
    return parsed.value;
}

/**
 * Resolves the coprincipal account MailboxInfo directly addressed by a route key prefix
 * (MBX / OID / PUID), without consulting the `ai`/`ds` query-param fallback.
 *
 * This is the shared "route key -> account" lookup consumed by both route-key resolution
 * (resolveMailboxFromRouteKeyInternal) and applink account-match classification
 * (classifyApplinkAccountMatch), so both interpret the MBX/OID/PUID prefixes identically.
 *
 * Note: explicit-logon applink classification is OWA-only, and OWA has no
 * shared-mailboxes-as-accounts (that is a Monarch concept). A single interpretation of the
 * route-key prefixes is therefore safe across both callers.
 *
 * @param routeKey The raw URL-encoded route key from the URL path
 * @param steps Optional StepsCustomData for per-prefix telemetry
 * @returns The MailboxInfo of the addressed coprincipal account, or undefined if the route key
 * is unparseable or no matching account is found
 */
export function resolveCoprincipalMailboxInfoFromRouteKey(routeKey: string, steps?: StepsCustomData): MailboxInfo | undefined {
    const parsedRouteKey = parseRouteKey(routeKey);
    if (!parsedRouteKey) {
        return undefined;
    }
    
    // MBX: prefix — direct GUID lookup
    if (parsedRouteKey.prefix === "MBX") {
        const account = getCoprincipalAccountByMailboxGuid(parsedRouteKey.value);
        steps?.addStep("CoprincipalAccountByMailboxGuid", {
            accountFound: !!account,
            accountType: account?.mailboxInfo.type,
        });
        return account?.mailboxInfo;
    }
    
    // OID: prefix — resolve by Object ID + tenant
    if (parsedRouteKey.prefix === "OID" && parsedRouteKey.tenantId) {
        const coprincipalAccounts = getCoprincipalAccountsByOidAndTid(parsedRouteKey.value, parsedRouteKey.tenantId);
        steps?.addStep("OidRouteKeyLookup", { accountsFound: coprincipalAccounts.length });
        return getAuthenticatingAccountMailboxInfo(coprincipalAccounts);
    }
    
    // PUID: prefix — resolve by Passport Unique ID + tenant
    if (parsedRouteKey.prefix === "PUID" && parsedRouteKey.tenantId) {
        const coprincipalAccounts = getCoprincipalAccountsByPuidAndTid(parsedRouteKey.value, parsedRouteKey.tenantId);
        steps?.addStep("PuidRouteKeyLookup", { accountsFound: coprincipalAccounts.length });
        return getAuthenticatingAccountMailboxInfo(coprincipalAccounts);
    }
    
    return undefined;
}

/**
 * Parsed components of the `ai` (authenticating identity) query parameter.
 * Supported formats:
 * - `<ID>@tenantID`
 * - `OID:<ID>@tenantID`
 * - `PUID:<ID>@tenantID`
 *
 * For backward compatibility we also tolerate `<OID>/<legacy>@tenantID` and
 * use the first segment as the OID.
 */
interface AuthenticatingIdentity {
    /** The identity value (either an OID or a PUID depending on identityType) */
    id: string;
    tenantId: string;
    /** Indicates how the identity should be looked up. 'oid' for OID-based or unprefixed values, 'puid' for PUID-prefixed values. */
    identityType: "oid" | "puid";
}

export type { AuthenticatingIdentity };

function decodePossiblyEncodedValue(value: string | undefined): string | undefined {
    if (!value) {
        return undefined;
    }
    
    try {
        return decodeURIComponent(value);
    }
    catch {
        // Query params may already be decoded by the parser; preserve original value on decode errors.
        return value;
    }
}

/**
 * Parses the `ai` query parameter value into its component parts.
 * Expected primary format: `<ID>@tenantID` (already URL-decoded by query string parser)
 * Prefixes `OID:` and `PUID:` are recognized and preserved so the caller can
 * perform the correct type of lookup.
 * @param ai The raw `ai` query parameter value
 * @returns Parsed identity components, or undefined if the format is invalid
 */
export function parseAuthenticatingIdentity(ai: string | undefined): AuthenticatingIdentity | undefined {
    if (!ai) {
        return undefined;
    }
    
    const atIndex = ai.lastIndexOf("@");
    if (atIndex === -1) {
        return undefined;
    }
    
    const tenantId = ai.substring(atIndex + 1);
    const rawIdentity = ai.substring(0, atIndex);
    if (!tenantId || !rawIdentity) {
        return undefined;
    }
    
    // Back-compat: old values could be OID/legacyIdentifier@tenant.
    const firstIdentitySegment = rawIdentity.split("/")[0];
    if (!firstIdentitySegment) {
        return undefined;
    }
    
    // If the segment contains a colon, it must be a recognized prefix (OID: or PUID:).
    // Unrecognized prefixed formats (e.g. SMTP:) are rejected to avoid misinterpreting
    // the value as a bare OID.
    const prefixMatch = firstIdentitySegment.match(/^(OID|PUID):(.*)$/i);
    if (!prefixMatch && firstIdentitySegment.includes(":")) {
        return undefined;
    }
    
    const identityType: "oid" | "puid" = prefixMatch && prefixMatch[1].toUpperCase() === "PUID" ? "puid" : "oid";
    const id = (prefixMatch ? prefixMatch[2] : firstIdentitySegment).trim();
    
    if (!id) {
        return undefined;
    }
    
    return { id, tenantId, identityType };
}

/**
 * Given an authenticating account's MailboxInfo, attempts to resolve a destination
 * mailbox that lives under that account (e.g., a shared mailbox or archive).
 * @param authenticatingMailboxInfo The MailboxInfo of the authenticating coprincipal account
 * @param destinationSmtpAddress The SMTP address of the destination mailbox
 * @returns The destination MailboxInfo if found under the account, or undefined
 */
function resolveMailboxFromAuthenticatingAccount(authenticatingMailboxInfo: MailboxInfo, destinationSmtpAddress: string): MailboxInfo | undefined {
    const sharedMatch = getSharedMailboxInfo(authenticatingMailboxInfo, destinationSmtpAddress);
    if (sharedMatch) {
        return sharedMatch;
    }
    
    const archiveMatch = getArchiveMailboxInfo(authenticatingMailboxInfo, destinationSmtpAddress);
    if (archiveMatch) {
        return archiveMatch;
    }
    
    return undefined;
}

/**
 * Determines whether the destination SMTP address corresponds to a shared mailbox that has been
 * added as its own coprincipal account. Such accounts authenticate under the authenticated account's identity,
 * so they appear in the OID/PUID+TID lookup alongside the primary account but are distinguished by
 * their own `mailboxSmtpAddress`.
 * @param coprincipalAccounts The coprincipal accounts sharing the authenticating identity
 * @param destinationSmtpAddress The SMTP address of the destination mailbox (`ds` query parameter)
 * @returns The standalone account's MailboxInfo if a match is found, or undefined
 */
function resolveDestinationIfSharedAsCoprincipal(coprincipalAccounts: CoprincipalAccountSource[], destinationSmtpAddress: string): MailboxInfo | undefined {
    const target = destinationSmtpAddress.toLowerCase();
    const match = coprincipalAccounts.find(account => account.mailboxInfo.mailboxSmtpAddress?.toLowerCase() === target);
    
    return match?.mailboxInfo;
}

export async function resolveMailboxFromRouteKey(routeKey: string, scenario: MailboxRoutingScenario): Promise<MailboxInfo | undefined> {
    const steps = new StepsCustomData();
    const routeKeyType = getUniversalLinkRouteKeyType(routeKey);
    let resolvedMailboxInfo: MailboxInfo | undefined = undefined;
    
    if (!isGlobalFeatureEnabled("acct-resolvemailboxfromroutekey")) {
        return undefined;
    }
    
    try {
        resolvedMailboxInfo = await resolveMailboxFromRouteKeyInternal(routeKey, steps);
        
        // When coming from an applink (resolvedMailboxInfo present), wait for the associated
        // account to finish booting before launching compose. This prevents launching the action on
        // an account that hasn't finished booting.
        if (resolvedMailboxInfo) {
            await waitForAccountToBeStarted(resolvedMailboxInfo, 150000 /* timeout in ms */);
            steps.addStep("WaitForAccountToBeStartedComplete");
        }
        
        logUsage("ResolveMailboxFromRouteKey", resolvedMailboxInfo ?? tryGetGlobalSettingsAccountMailboxInfo(), {
            scenario,
            routeKeyType,
            resolvedMailboxInfo: !!resolvedMailboxInfo,
            resolvedMailboxType: resolvedMailboxInfo?.type,
            diagnosticData: resolvedMailboxInfo?.diagnosticData,
            isExplicitLogon: resolvedMailboxInfo?.isExplicitLogon,
            ...steps.getCustomDataMapForSteps(),
        });
    }
    catch (error) {
        logGreyError("ResolveMailboxFromRouteKey_Failed", resolvedMailboxInfo ?? tryGetGlobalSettingsAccountMailboxInfo(), error instanceof Error ? error : undefined, {
            scenario,
            routeKeyType,
            hasResolvedMailboxInfo: !!resolvedMailboxInfo,
            ...steps.getCustomDataMapForSteps(),
        });
    }
    
    return resolvedMailboxInfo;
}

/**
 * Resolves a MailboxInfo from Universal Link URL components.
 *
 * URL format: `<domain>/<module>/<routeKey>/applink/<path>/?ds=smtp&ai=<ID>@tenantID`
 *
 * The routeKey supports the following prefixes:
 * - `MBX:<mailboxGuid>@<tenantId>` — direct mailbox GUID lookup
 * - `OID:<objectId>@<tenantId>` — AAD Object ID + tenant lookup
 * - `PUID:<puid>@<tenantId>` — Passport Unique ID + tenant lookup
 *
 * Resolution order:
 * 1. routeKey prefix detection (MBX, OID, or PUID)
 * 2. MBX → extract GUID → getCoprincipalAccountByMailboxGuid
 * 3. OID → getCoprincipalAccountsByOidAndTid → return coprincipal mailboxInfo
 * 4. PUID → getCoprincipalAccountsByPuidAndTid → return coprincipal mailboxInfo
 * 5. Route key miss (no direct match, or an unsupported prefix such as `SMTP:` that the direct
 *    lookup can't resolve) → ai query param (authenticating identity + tenantId) →
 *    getCoprincipalAccountsByOid or getCoprincipalAccountsByPuid depending on prefix
 * 6. ds query param (destination SMTP) matches a coprincipal account sharing the authenticating
 *    identity → shared mailbox added as its own account
 * 7. ds query param (destination SMTP) under the authenticating account → shared/archive lookup
 * 8. Returns undefined if no match found (callers handle fallback actions)
 *
 * @param routeKey The `:routeKey` path parameter (URL-encoded prefixed identifier)
 * @param steps StepsCustomData object for tracking resolution step progress
 * @returns The resolved MailboxInfo, or undefined if no matching account was found
 */
async function resolveMailboxFromRouteKeyInternal(routeKey: string, steps: StepsCustomData): Promise<MailboxInfo | undefined> {
    // A route key is the primary target identifier — never resolve without one. (Callers already
    // guard, but this keeps the ai/ds fallback below from running against an empty route key.)
    if (!routeKey) {
        return undefined;
    }
    
    const parsedRouteKey = parseRouteKey(routeKey);
    steps.addStep("ParseRouteKey", {
        prefix: parsedRouteKey?.prefix,
        hasValue: !!parsedRouteKey?.value,
        hasTenantId: !!parsedRouteKey?.tenantId,
    });
    
    // 1–3. Direct route-key account lookup (MBX / OID / PUID). When the route key can't be
    // resolved directly — including unsupported prefixes like `SMTP:` that parseRouteKey rejects —
    // we intentionally fall through to the ai/ds fallback below rather than bailing, so a link
    // whose route key is an SMTP address can still resolve via its authenticating identity (ai).
    const directMatch = resolveCoprincipalMailboxInfoFromRouteKey(routeKey, steps);
    if (directMatch) {
        return directMatch;
    }
    
    // 4. Fallback — resolve via ai query param + ds sub-resource lookup
    const rawAi = getQueryStringParameter("ai");
    const ai = decodePossiblyEncodedValue(rawAi);
    const identity = parseAuthenticatingIdentity(ai);
    steps.addStep("ParseAuthenticatingIdentity", {
        aiProvided: !!rawAi,
        aiDecoded: !!rawAi && rawAi !== ai,
        identityParsed: !!identity,
    });
    
    if (identity) {
        steps.addStep("AuthenticatingIdentityLookup", { identityType: identity.identityType });
        const coprincipalAccounts = identity.identityType === "puid"
            ? getCoprincipalAccountsByPuidAndTid(identity.id, identity.tenantId)
            : getCoprincipalAccountsByOidAndTid(identity.id, identity.tenantId);
        steps.addStep("CoprincipalAccountByIdentity", {
            identityType: identity.identityType,
            accountsFound: coprincipalAccounts.length,
        });
        
        const authMailboxInfo = getAuthenticatingAccountMailboxInfo(coprincipalAccounts);
        steps.addStep("AuthenticatingAccountMailboxInfo", { authAccountFound: !!authMailboxInfo });
        
        if (authMailboxInfo) {
            // Check if the destination is a sub-resource (shared/archive) under the auth account
            const rawDestinationSmtp = getQueryStringParameter("ds");
            const destinationSmtp = decodePossiblyEncodedValue(rawDestinationSmtp);
            steps.addStep("DestinationSmtpFromQuery", {
                destinationSmtpProvided: !!rawDestinationSmtp,
                destinationSmtpDecoded: !!rawDestinationSmtp && rawDestinationSmtp !== destinationSmtp,
            });
            if (destinationSmtp) {
                // Attempt to resolve `ds` to that the Coprincipal Accounts in case of Shared as Account
                // before attempting to find it within the shared/archive sub-resources under the auth account.
                const sharedAsCoprincipal = resolveDestinationIfSharedAsCoprincipal(coprincipalAccounts, destinationSmtp);
                steps.addStep("ResolveDestinationAsCoprincipalAccount", {
                    resolvedAsAccount: !!sharedAsCoprincipal,
                });
                if (sharedAsCoprincipal) {
                    return sharedAsCoprincipal;
                }
                
                // Ensure shared mailbox data is loaded before attempting resolution —
                // on initial navigation, sharedSources may not yet be fetched from the server
                await lazyEnsureSharedMailboxDataForMailboxInfo.importAndExecute(authMailboxInfo);
                steps.addStep("EnsureSharedMailboxDataForMailboxInfo");
                
                const resolvedMailbox = resolveMailboxFromAuthenticatingAccount(authMailboxInfo, destinationSmtp);
                steps.addStep("ResolveMailboxFromAuthenticatingAccount", {
                    hasResolvedMailbox: !!resolvedMailbox,
                    resolvedMailboxType: resolvedMailbox?.type,
                });
                
                return resolvedMailbox;
            }
            
            // No destination SMTP provided — caller is responsible for fallback actions
            return undefined;
        }
    }
    
    // No matching account found — caller is responsible for fallback actions
    return undefined;
}

function getAuthenticatingAccountMailboxInfo(coprincipalAccounts: CoprincipalAccountSource[]): MailboxInfo | undefined {
    if (coprincipalAccounts.length === 0) {
        return undefined;
    }
    
    if (coprincipalAccounts.length === 1) {
        return coprincipalAccounts[0].mailboxInfo;
    }
    
    // If multiple coprincipal accounts share the same OID+TID, we can operate under the
    // assumption that they share the same accountKey and we can return the MailboxInfo
    // belonging to the CoprincipalAccountSource of this accountKey
    const accountKey = getAccountKeyForSourceId(coprincipalAccounts[0].sourceId);
    
    return getCoprincipalAccountForAccountKey(accountKey)?.mailboxInfo;
}

export type MailboxRoutingScenario = "ComposeFromApplink" | "ReadFromApplink";
